import type { ReactNode } from 'react';
import { BackButton } from './BackButton';
import styles from './Screen.module.css';

/**
 * Standard screen frame: an optional Back affordance, a level-1 heading, and
 * a content region.
 *
 * Every screen goes through this so heading order stays correct and page
 * structure is consistent for assistive technology. `backTo` is opt-in and
 * deliberately not inferred from routing depth — top-level tabs (Dashboard,
 * Plans, Exercises, Progress, Settings) pass nothing and get no Back button,
 * matching "no pointless Back button that just returns to Home"; every
 * child/detail screen passes its real parent route explicitly, per the
 * fixed LiftX navigation hierarchy (plan editor → Training Plans, exercise
 * detail → Exercises, etc.) — UI/navigation polish pass.
 */
export function Screen({
  title,
  subtitle,
  backTo,
  backLabel,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Parent route to return to. Omit for top-level tabs. */
  backTo?: string;
  /** Defaults to "Back" — pass e.g. "Training Plans" for more specific context. */
  backLabel?: string;
  children?: ReactNode;
}): React.JSX.Element {
  return (
    <section className={styles.screen} aria-labelledby="screen-title">
      {backTo !== undefined && <BackButton to={backTo} label={backLabel ?? 'Back'} />}
      <header className={styles.header}>
        <h1 id="screen-title" className={styles.title}>
          {title}
        </h1>
        {subtitle !== undefined && <p className={styles.subtitle}>{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

/**
 * A Phase 1 placeholder.
 *
 * States plainly which phase brings the real screen, so the shell is never
 * mistaken for unfinished work.
 */
export function PhasePlaceholder({ phase }: { phase: number }): React.JSX.Element {
  return (
    <div className={styles.placeholder} role="note">
      <p className={styles.placeholderText}>
        This area is part of the LiftX foundation. Its functionality arrives in{' '}
        <strong>Phase {phase}</strong>.
      </p>
    </div>
  );
}
